(() => {
  const formatMoney = (cents, symbol = '$') => {
    const amount = new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents || 0) / 100);
    return `${symbol}${amount}`;
  };

  const formatPerUnit = (cents, symbol = '$') => {
    const amount = new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(Number(cents || 0) / 100);
    return `${symbol}${amount}`;
  };

  const formatCurrentPrice = (cents, symbol = '$') => {
    const formattedPrice = formatMoney(cents, symbol);
    const decimalPosition = formattedPrice.lastIndexOf('.');

    if (decimalPosition === -1) return formattedPrice;

    return `<span>${formattedPrice.slice(0, decimalPosition + 1)}</span><small>${formattedPrice.slice(decimalPosition + 1)}</small>`;
  };

  const copyProductUrl = async (url) => {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
      return;
    }

    const input = document.createElement('textarea');
    input.value = url;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.top = '-9999px';
    document.body.appendChild(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    if (!copied) throw new Error('Unable to copy product URL.');
  };

  document.addEventListener('click', async (event) => {
    const shareButton = event.target.closest('[data-product-share]');
    if (!shareButton) return;

    event.preventDefault();
    const shareData = {
      title: shareButton.dataset.shareTitle || document.title,
      url: window.location.href,
    };

    if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    const label = shareButton.querySelector('[data-product-share-label]');
    const originalLabel = label?.textContent || 'Share this product';
    try {
      await copyProductUrl(shareData.url);
      if (label) label.textContent = 'Link copied';
    } catch (error) {
      if (label) label.textContent = 'Copy failed';
    }
    window.setTimeout(() => {
      if (label) label.textContent = originalLabel;
    }, 2000);
  });

  const initBusinessCardProduct = (root = document) => {
    root.querySelectorAll('[data-business-card-product]').forEach((section) => {
      if (section.dataset.businessCardInitialized === 'true') return;

      const variants = JSON.parse(section.querySelector('[data-business-card-variants]')?.textContent || '[]');
      const quantityIndex = Number(section.dataset.quantityOptionIndex);
      const quantitySelect = () => section.querySelector('[data-quantity-select]');
      const drawer = section.querySelector('[data-volume-drawer]');
      const pricingList = section.querySelector('[data-pricing-list]');
      const currencySymbol = section.dataset.currencySymbol || '$';
      let pendingQuantity = quantitySelect()?.value || '';
      const observedAfterpayRoots = new WeakSet();

      const applyAfterpayStyles = () => {
        section.querySelectorAll('square-placement').forEach((placement) => {
          const root = placement.shadowRoot;
          if (!root) return;

          if (!observedAfterpayRoots.has(root)) {
            const observer = new MutationObserver(() => {
              if (!root.querySelector('[data-business-card-afterpay-styles]')) applyAfterpayStyles();
            });
            observer.observe(root, { childList: true });
            observedAfterpayRoots.add(root);
          }

          if (root.querySelector('[data-business-card-afterpay-styles]')) return;

          const style = document.createElement('style');
          style.setAttribute('data-business-card-afterpay-styles', '');
          style.textContent = `
            @media (max-width: 749px) {
              .afterpay-main-text,
              .afterpay-main-text * {
                font-family: Inter, sans-serif !important;
                font-size: 12px !important;
                line-height: 19px !important;
                letter-spacing: 0 !important;
              }
              .afterpay-logo.type-badge,
              .afterpay-logo.type-badge svg,
              .afterpay-logo.logo-cashappafterpay,
              .afterpay-logo.logo-cashappafterpay svg {
                width: auto !important;
                height: 20px !important;
                max-height: 20px !important;
              }
            }
          `;
          root.appendChild(style);
        });
      };

      if (window.customElements?.whenDefined) {
        window.customElements.whenDefined('square-placement').then(() => {
          applyAfterpayStyles();
          window.setTimeout(applyAfterpayStyles, 250);
        });
      }
      applyAfterpayStyles();
      [500, 1000, 2000].forEach((delay) => window.setTimeout(applyAfterpayStyles, delay));

      const bindMobileGallery = () => {
        const gallery = section.querySelector('[data-business-mobile-gallery]');
        const main = gallery?.querySelector('[data-business-mobile-main]');
        const thumbnailRail = gallery?.querySelector('[data-business-mobile-thumbnails]');
        const slides = [...(gallery?.querySelectorAll('[data-business-mobile-slide]') || [])];
        const thumbs = [...(gallery?.querySelectorAll('[data-business-mobile-thumb]') || [])];
        const dots = [...(gallery?.querySelectorAll('.business-card-product__mobile-dots i') || [])];
        if (!gallery || !main || !slides.length) return;

        let activeIndex = 0;
        let scrollTimer;
        const updateThumbnailOverflow = () => {
          if (!thumbnailRail) return;
          gallery.classList.toggle(
            'has-thumbnail-overflow',
            thumbnailRail.scrollWidth > thumbnailRail.clientWidth + 1
          );
        };
        const activate = (index, scrollMain = true) => {
          activeIndex = Math.max(0, Math.min(index, slides.length - 1));
          thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === activeIndex));
          dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
          const activeThumb = thumbs[activeIndex];
          if (thumbnailRail && activeThumb) {
            const thumbLeft = activeThumb.offsetLeft - ((thumbnailRail.clientWidth - activeThumb.offsetWidth) / 2);
            thumbnailRail.scrollTo({ left: Math.max(0, thumbLeft), behavior: 'smooth' });
          }
          if (scrollMain) main.scrollTo({ left: activeIndex * main.clientWidth, behavior: 'smooth' });
        };

        thumbs.forEach((thumb) => thumb.addEventListener('click', () => activate(Number(thumb.dataset.businessMobileThumb))));
        gallery.querySelector('[data-business-mobile-prev]')?.addEventListener('click', () => activate(activeIndex - 1));
        gallery.querySelector('[data-business-mobile-next]')?.addEventListener('click', () => activate(activeIndex + 1));
        main.addEventListener('scroll', () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => {
            const index = Math.round(main.scrollLeft / Math.max(main.clientWidth, 1));
            activate(index, false);
          }, 80);
        }, { passive: true });
        window.addEventListener('resize', updateThumbnailOverflow, { passive: true });
        window.requestAnimationFrame(updateThumbnailOverflow);
        window.addEventListener('load', updateThumbnailOverflow, { once: true });
      };

      const bindQuickBar = () => {
        const quickBar = section.querySelector('.business-card-product__quick-bar');
        const primaryAddButton = section.querySelector(`#ProductSubmitButton-${section.dataset.section}`);
        if (!quickBar || !primaryAddButton) return;

        let ticking = false;

        const updateStickyState = () => {
          const buttonRect = primaryAddButton.getBoundingClientRect();
          const shouldShow = window.scrollY > 300 &&
            (buttonRect.top > window.innerHeight || buttonRect.bottom < 0);
          quickBar.classList.toggle('is-scroll-visible', shouldShow);
          quickBar.setAttribute('aria-hidden', String(!shouldShow));
          quickBar.toggleAttribute('inert', !shouldShow);
          ticking = false;
        };

        window.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(updateStickyState);
        }, { passive: true });

        window.addEventListener('resize', updateStickyState, { passive: true });

        const primaryButtonObserver = new IntersectionObserver(() => {
          updateStickyState();
        }, { threshold: 0.05 });

        primaryButtonObserver.observe(primaryAddButton);
        updateStickyState();
      };

      const bindProductScroll = () => {
        const layout = section.querySelector('.business-card-product__layout');
        const gallery = section.querySelector('.business-card-product__gallery');
        const details = section.querySelector('.business-card-product__details');
        if (!layout || !gallery || !details) return;

        let frame;
        const update = () => {
          window.cancelAnimationFrame(frame);
          frame = window.requestAnimationFrame(() => {
            gallery.style.top = '';
            details.style.top = '';
            gallery.classList.remove('is-product-scroll-sticky');
            details.classList.remove('is-product-scroll-sticky');

            if (!window.matchMedia('(min-width: 990px)').matches) return;

            const galleryHeight = gallery.offsetHeight;
            const detailsHeight = details.offsetHeight;
            const viewportHeight = window.innerHeight;
            const bottomClearance = 40;
            let stickyElement = null;

            if (galleryHeight > detailsHeight) stickyElement = details;
            else if (detailsHeight > galleryHeight) stickyElement = gallery;

            if (stickyElement) {
              const stickyOffset = Math.max(stickyElement.offsetHeight - viewportHeight + bottomClearance, 0);
              stickyElement.style.top = `${-stickyOffset}px`;
              stickyElement.classList.add('is-product-scroll-sticky');
            }
          });
        };

        const observer = new ResizeObserver(update);
        observer.observe(gallery);
        observer.observe(details);
        window.addEventListener('resize', update, { passive: true });
        update();
      };

      const bindAccordions = () => {
        const accordions = [...section.querySelectorAll('.business-card-product__accordion')];
        const animations = new WeakMap();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let initiallyOpen = null;

        const setAccordionOpen = (accordion, shouldOpen) => {
          const content = accordion.querySelector('.business-card-product__accordion-content');
          animations.get(accordion)?.cancel();

          if (!content || reduceMotion.matches) {
            accordion.open = shouldOpen;
            return;
          }

          if (shouldOpen) accordion.open = true;
          const startHeight = shouldOpen ? 0 : content.getBoundingClientRect().height;
          const endHeight = shouldOpen ? content.scrollHeight : 0;
          content.style.overflow = 'hidden';

          const animation = content.animate(
            [
              { height: `${startHeight}px`, opacity: shouldOpen ? 0 : 1 },
              { height: `${endHeight}px`, opacity: shouldOpen ? 1 : 0 },
            ],
            { duration: 300, easing: 'ease-in-out' },
          );
          animations.set(accordion, animation);
          animation.onfinish = () => {
            if (!shouldOpen) accordion.open = false;
            content.style.removeProperty('overflow');
            animations.delete(accordion);
          };
        };

        accordions.forEach((accordion) => {
          if (accordion.open) {
            if (initiallyOpen) accordion.open = false;
            else initiallyOpen = accordion;
          }

          accordion.querySelector('summary')?.addEventListener('click', (event) => {
            event.preventDefault();
            const shouldOpen = !accordion.open;
            if (shouldOpen) {
              accordions.forEach((otherAccordion) => {
                if (otherAccordion !== accordion && otherAccordion.open) {
                  setAccordionOpen(otherAccordion, false);
                }
              });
            }
            setAccordionOpen(accordion, shouldOpen);
          });
        });
      };

      const selectedValues = () =>
        [...section.querySelectorAll('variant-selects fieldset input:checked, variant-selects select')].map((input) =>
          input.tagName === 'SELECT' ? input.value : input.value
        );

      const selectedVariant = () => {
        const values = selectedValues();
        return variants.find((variant) => variant.options.every((option, index) => option === values[index]));
      };

      const stickyOptionSelects = () => [...section.querySelectorAll('[data-sticky-option-select]')];

      const updateStickyAvailability = () => {
        const selects = stickyOptionSelects();
        const selected = selectedValues();
        selects.forEach((select) => {
          const optionIndex = Number(select.dataset.optionIndex);
          [...select.options].forEach((option) => {
            const available = variants.some((variant) => variant.available && variant.options.every((value, index) => {
              if (index === optionIndex) return value === option.value;
              if (index < optionIndex) return !selected[index] || value === selected[index];
              return true;
            }));
            option.disabled = !available;
            option.textContent = `${option.value}${available ? '' : ' — Sold out'}`;
          });
        });
      };

      const syncStickyOptions = () => {
        const values = selectedValues();
        stickyOptionSelects().forEach((select) => {
          const value = values[Number(select.dataset.optionIndex)];
          if (value && [...select.options].some((option) => option.value === value)) select.value = value;
        });
        updateStickyAvailability();
      };

      const selectMainOption = (optionIndex, value) => {
        const optionControls = [...section.querySelectorAll('variant-selects fieldset, variant-selects .business-card-product__quantity-option')];
        const group = optionControls[Number(optionIndex)];
        const radio = [...(group?.querySelectorAll('input[type="radio"]') || [])].find((input) => input.value === value);
        const select = group?.querySelector('select');
        if (radio) radio.click();
        else if (select) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      const updateCalculatedPrices = () => {
        const variant = selectedVariant();
        const quantity = Math.max(1, Number(quantitySelect()?.value || 1));
        const unitPrice = section.querySelector('[data-unit-price]');
        const stickyPrice = section.querySelector('[data-sticky-price-amount]');
        const currentPrice = section.querySelector('[data-current-price]');
        const quantityTriggerValue = section.querySelector('[data-quantity-trigger-value]');
        const compatibleVariants = compatibleQuantityVariants();
        const minimumQuantity = compatibleVariants[0]?.options[quantityIndex] || quantitySelect()?.options[0]?.value;

        if (unitPrice && variant) unitPrice.textContent = `${formatMoney(variant.price / quantity, currencySymbol)} each / ${quantity} units`;
        if (currentPrice && variant) currentPrice.innerHTML = formatCurrentPrice(variant.price, currencySymbol);
        if (quantityTriggerValue) quantityTriggerValue.textContent = quantity;
        if (minimumQuantity) {
          section.querySelectorAll('[data-quantity-menu-minimum]').forEach((minimum) => {
            minimum.textContent = minimumQuantity;
          });
        }
        if (stickyPrice && variant) stickyPrice.textContent = formatMoney(variant.price, currencySymbol);
        if (variant) {
          section.querySelectorAll('square-placement').forEach((placement) => {
            placement.setAttribute('data-amount', (Number(variant.price) / 100).toFixed(2));
          });
        }
        window.setTimeout(applyAfterpayStyles, 100);
      };

      const compatibleQuantityVariants = () => {
        const values = selectedValues();
        return variants
          .filter((variant) =>
            variant.available && variant.options.every((option, index) => index === quantityIndex || option === values[index])
          )
          .sort((a, b) => Number(a.options[quantityIndex]) - Number(b.options[quantityIndex]));
      };

      const bindPricingKeyboard = (container) => {
        if (!container || container.dataset.keyboardBound === 'true') return;
        container.dataset.keyboardBound = 'true';
        container.addEventListener('keydown', (event) => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          const rows = [...container.querySelectorAll('.business-card-product__pricing-row')];
          if (!rows.length) return;
          event.preventDefault();
          const current = event.target.closest('.business-card-product__pricing-row')
            || container.querySelector('.business-card-product__pricing-row.is-selected');
          const currentIndex = Math.max(0, rows.indexOf(current));
          const nextIndex = event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? rows.length - 1
              : event.key === 'ArrowDown'
                ? Math.min(currentIndex + 1, rows.length - 1)
                : Math.max(currentIndex - 1, 0);
          const nextRow = rows[nextIndex];
          nextRow.click();
          nextRow.focus({ preventScroll: true });
          nextRow.scrollIntoView({ block: 'nearest' });
        });
      };

      const renderPricingRows = (container, selectImmediately = false) => {
        if (!container || Number.isNaN(quantityIndex)) return;
        const selectedQuantity = quantitySelect()?.value;
        const configuredRecommended = drawer?.dataset.recommendedQuantity || '';
        const compatibleVariants = compatibleQuantityVariants();
        const maximumQuantity = compatibleVariants.length
          ? compatibleVariants[compatibleVariants.length - 1].options[quantityIndex]
          : '';
        const formattedMaximumQuantity = Number.isFinite(Number(maximumQuantity))
          ? new Intl.NumberFormat(document.documentElement.lang || 'en-NZ').format(Number(maximumQuantity))
          : maximumQuantity;
        section.querySelectorAll('[data-pricing-max]').forEach((maximum) => {
          maximum.textContent = formattedMaximumQuantity;
        });
        if (compatibleVariants.length) {
          section.querySelectorAll('[data-quantity-menu-minimum]').forEach((minimum) => {
            minimum.textContent = compatibleVariants[0].options[quantityIndex];
          });
        }
        container.innerHTML = '';

        compatibleVariants.forEach((variant) => {
          const quantity = variant.options[quantityIndex];
          const isRecommended = quantity === configuredRecommended;
          const row = document.createElement('button');
          row.type = 'button';
          row.className = `business-card-product__pricing-row${quantity === selectedQuantity ? ' is-selected' : ''}${isRecommended ? ' is-recommended' : ''}`;
          row.dataset.quantity = quantity;
          row.setAttribute('aria-pressed', String(quantity === selectedQuantity));
          const perUnitPrice = selectImmediately
            ? formatPerUnit(variant.price / Math.max(1, Number(quantity)), currencySymbol)
            : `${formatMoney(variant.price / Math.max(1, Number(quantity)), currencySymbol)} each`;
          row.innerHTML = `<span>${quantity}${isRecommended ? '<small class="business-card-product__recommended">Recommended</small>' : ''}</span><span>${formatMoney(variant.price, currencySymbol)}</span><span>${perUnitPrice}</span>`;
          row.addEventListener('click', () => {
            container.querySelectorAll('.business-card-product__pricing-row').forEach((item) => {
              item.classList.remove('is-selected');
              item.setAttribute('aria-pressed', 'false');
            });
            row.classList.add('is-selected');
            row.setAttribute('aria-pressed', 'true');
            if (selectImmediately) {
              const select = quantitySelect();
              if (select) {
                select.value = quantity;
                select.dispatchEvent(new Event('change', { bubbles: true }));
              }
              closeQuantityMenus();
            } else {
              pendingQuantity = quantity;
            }
          });
          container.appendChild(row);
        });
        bindPricingKeyboard(container);
      };

      const closeQuantityMenus = () => {
        section.querySelectorAll('[data-quantity-menu]').forEach((menu) => menu.setAttribute('hidden', ''));
        section.querySelectorAll('[data-quantity-menu-trigger]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      };

      const toggleQuantityMenu = (trigger) => {
        const control = trigger.closest('.business-card-product__quantity-control');
        const menu = control?.querySelector('[data-quantity-menu]');
        const list = control?.querySelector('[data-quantity-menu-list]');
        if (!menu) return;
        const shouldOpen = menu.hasAttribute('hidden');
        closeQuantityMenus();
        if (shouldOpen) {
          renderPricingRows(list, true);
          menu.scrollTop = 0;
          menu.removeAttribute('hidden');
          trigger.setAttribute('aria-expanded', 'true');
          window.requestAnimationFrame(() => {
            (list?.querySelector('.is-selected') || list?.querySelector('.business-card-product__pricing-row'))?.focus();
          });
        }
      };

      const openDrawer = () => {
        if (!drawer) return;
        pendingQuantity = quantitySelect()?.value || '';
        renderPricingRows(pricingList, false);
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('business-card-product-drawer-open');
        window.requestAnimationFrame(() => {
          (pricingList?.querySelector('.is-selected') || pricingList?.querySelector('.business-card-product__pricing-row'))?.focus();
        });
      };

      const closeDrawer = () => {
        if (!drawer) return;
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('business-card-product-drawer-open');
      };

      section.addEventListener('change', (event) => {
        if (event.target.matches('[data-sticky-option-select]')) {
          selectMainOption(event.target.dataset.optionIndex, event.target.value);
          window.requestAnimationFrame(syncStickyOptions);
          return;
        }
        if (event.target.matches('variant-selects input, variant-selects select')) {
          closeQuantityMenus();
          window.requestAnimationFrame(() => {
            updateCalculatedPrices();
            syncStickyOptions();
          });
        }
        if (event.target.matches('[data-artwork-input]')) {
          const label = event.target.closest('.business-card-product__artwork-field')?.querySelector('[data-upload-label]')
            || section.querySelector('[data-upload-label]');
          if (label && event.target.files?.[0]) label.textContent = event.target.files[0].name;
        }
      });

      section.addEventListener('click', (event) => {
        const quantityTrigger = event.target.closest('[data-quantity-menu-trigger]');
        if (quantityTrigger) {
          toggleQuantityMenu(quantityTrigger);
          return;
        }
        if (event.target.closest('[data-volume-open]')) {
          openDrawer();
          return;
        }
        if (event.target.closest('[data-volume-close]')) {
          closeDrawer();
          return;
        }
        if (event.target.closest('[data-volume-confirm]')) {
          const select = quantitySelect();
          if (select && pendingQuantity) {
            select.value = pendingQuantity;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          closeDrawer();
          return;
        }
        if (!event.target.closest('.business-card-product__quantity-control')) closeQuantityMenus();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        closeQuantityMenus();
        if (drawer?.classList.contains('is-open')) closeDrawer();
      });

      const pickupAvailability = section.querySelector('pickup-availability');
      const syncPickupSummary = () => {
        const info = pickupAvailability?.querySelector('.pickup-availability-info');
        const title = info?.querySelector('.caption-large');
        const time = info?.querySelector('.caption');
        const summaryTitle = section.querySelector('[data-pickup-summary-title]');
        const summaryTime = section.querySelector('[data-pickup-summary-time]');
        if (title && summaryTitle) summaryTitle.textContent = title.textContent.trim();
        if (summaryTime) {
          summaryTime.textContent = time?.textContent.trim() || '';
          summaryTime.toggleAttribute('hidden', !time);
        }
      };
      if (pickupAvailability) {
        new MutationObserver(syncPickupSummary).observe(pickupAvailability, { childList: true, subtree: true });
        syncPickupSummary();
      }

      const finishZoomMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
      section.querySelectorAll('.business-card-product__finish-image').forEach((imageWrapper) => {
        const image = imageWrapper.querySelector('img');
        if (!image) return;

        let lens;
        const zoomRatio = 2.25;
        const removeLens = () => {
          lens?.remove();
          lens = null;
        };
        const moveLens = (event) => {
          if (!lens) return;
          const bounds = imageWrapper.getBoundingClientRect();
          const x = Math.max(0, Math.min(event.clientX - bounds.left, bounds.width));
          const y = Math.max(0, Math.min(event.clientY - bounds.top, bounds.height));
          const sourceWidth = image.naturalWidth || image.width;
          const sourceHeight = image.naturalHeight || image.height;

          lens.style.left = `${x}px`;
          lens.style.top = `${y}px`;
          lens.style.backgroundSize = `${bounds.width * zoomRatio}px ${bounds.height * zoomRatio}px`;
          lens.style.backgroundPosition = `${-(x * zoomRatio - lens.offsetWidth / 2)}px ${-(y * zoomRatio - lens.offsetHeight / 2)}px`;
          if (sourceWidth && sourceHeight) lens.style.backgroundImage = `url("${image.currentSrc || image.src}")`;
        };

        imageWrapper.addEventListener('pointerenter', (event) => {
          if (!finishZoomMedia.matches || lens) return;
          lens = document.createElement('span');
          lens.className = 'business-card-product__finish-zoom-lens';
          lens.setAttribute('aria-hidden', 'true');
          imageWrapper.appendChild(lens);
          moveLens(event);
        });
        imageWrapper.addEventListener('pointermove', moveLens);
        imageWrapper.addEventListener('pointerleave', removeLens);
        imageWrapper.addEventListener('pointercancel', removeLens);
      });

      bindQuickBar();
      bindProductScroll();
      bindMobileGallery();
      bindAccordions();
      syncStickyOptions();
      updateCalculatedPrices();
      section.dataset.businessCardInitialized = 'true';
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initBusinessCardProduct());
  else initBusinessCardProduct();

  document.addEventListener('shopify:section:load', (event) => initBusinessCardProduct(event.target));
})();
